import { motion } from "motion/react";
import { EASE_EXPO } from "../motion/primitives";
import { PlanLauncher } from "../plan/PlanLauncher";

const INK = "#0A0F0E";
const TEAL = "#2EE6D6";
const SECONDARY = "#46504D";
const PAPER = "#FFFFFF";

/*
 * Headline size, in units of the left column's width (cqi). The longer line,
 * "STUDENT VISA,", is 7.26em wide in Onest 900 at -0.045em, so 100/7.26 =
 * 13.8cqi fills the column; 13.3cqi leaves about 3% so a line never wraps on
 * its own. The svh cap keeps it sane on short screens and landscape phones.
 * Re-measure if the words, the font or the tracking change.
 */
const H1_SIZE = "[font-size:clamp(2.25rem,min(13.3cqi,12.5svh),6rem)]";

/** Enters from below with a fast start and a soft landing. */
const rise = (delay: number) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: EASE_EXPO },
});

/** One headline line, lifted out of a mask. */
function MaskLine({ children, delay }: { children: React.ReactNode; delay: number }) {
  return (
    <span className="block overflow-hidden pb-[0.06em] -mb-[0.06em]">
      <motion.span
        className="block will-change-transform"
        initial={{ y: "102%", rotate: 3 }}
        animate={{ y: "0%", rotate: 0 }}
        transition={{ duration: 1, delay, ease: EASE_EXPO }}
        style={{ transformOrigin: "0% 100%" }}
      >
        {children}
      </motion.span>
    </span>
  );
}

/**
 * Homepage hero, in Wise's shape: an oversized headline, one sentence, and
 * the one thing to do. Nothing else competes with the first question; the
 * details live in the sections below.
 *
 * Motion: the headline lifts out of a mask line by line and a highlighter
 * swipes under "Austria.". Nothing drifts, spins or glows.
 *
 * No time promise and no "a person writes it" (Noah, 14 Oct): the plan is
 * built from the student's own answers.
 */
export function HeroLanding() {
  return (
    <section
      className="relative isolate overflow-hidden lg:flex lg:min-h-[calc(100svh-73px)] lg:items-center"
      style={{ backgroundColor: PAPER }}
    >
      <div className="mx-auto grid w-full max-w-[1240px] grid-cols-1 items-center gap-6 px-5 pb-6 pt-5 sm:gap-10 sm:px-8 sm:pb-10 sm:pt-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,440px)] lg:gap-14 lg:py-[clamp(12px,2.5svh,40px)] xl:grid-cols-[minmax(0,1fr)_minmax(0,480px)]">
        {/* ── Left ── */}
        {/* Container query context: the headline sizes to this column. */}
        <div className="[container-type:inline-size]">
          {/* Set line by line, so the break always falls at the comma. Real
              text stays sentence case for search engines and screen readers. */}
          <h1
            className={`relative isolate uppercase ${H1_SIZE}`}
            style={{
              fontFamily: "'Onest', system-ui, -apple-system, sans-serif",
              fontWeight: 900,
              lineHeight: 0.9,
              letterSpacing: "-0.045em",
              color: INK,
            }}
          >
            <MaskLine delay={0.12}>Student&nbsp;visa,</MaskLine>{" "}
            <MaskLine delay={0.24}>
              to&nbsp;
              <span className="relative inline-block">
                <motion.span
                  aria-hidden="true"
                  className="absolute -inset-x-[0.06em] bottom-[0.06em] -z-10 h-[0.36em] origin-left"
                  style={{ backgroundColor: TEAL, borderRadius: "0.08em" }}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.7, delay: 0.85, ease: EASE_EXPO }}
                />
                Austria.
              </span>
            </MaskLine>
          </h1>

          <motion.p
            className="mt-[clamp(14px,2.6svh,24px)] max-w-[34rem] text-[18px] font-medium leading-[1.5] sm:text-[clamp(18px,2.6svh,21px)]"
            style={{ color: SECONDARY }}
            {...rise(0.45)}
          >
            Free help. Your personal plan, then every step until you arrive.
          </motion.p>

        </div>

        {/* ── Right: the plan's first question, then /start ── */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, delay: 0.2, ease: EASE_EXPO }}
          className="relative w-full"
        >
          <PlanLauncher />
          {/* The person behind the plan: a real face and a promise he keeps
              (every request reaches Noah as an email alert), centred under the card. */}
          <figure className="mt-5 flex items-center justify-center gap-3 px-2">
            <img src="/noah-portrait.jpg" alt="Noah Rodman, founder of Migrada" width={44} height={44} loading="lazy" className="h-11 w-11 shrink-0 rounded-full object-cover" />
            <figcaption className="leading-snug">
              <span className="block text-[15px] font-bold" style={{ color: INK }}>
                “From the first question to your first study week.”
              </span>
              <span className="block text-[13px]" style={{ color: SECONDARY }}>
                Noah Rodman, founder of Migrada
              </span>
            </figcaption>
          </figure>
        </motion.div>
      </div>
    </section>
  );
}
